import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Car, KeyRound, Wrench, Wallet, AlertTriangle, Clock, ArrowUpRight, CalendarClock, FileSignature } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CalendarStrip from "@/components/CalendarStrip";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Panel, StatusBadge } from "@/components/shared";
import { api, notifyErr, fmtMoney, fmtDateTime, fmtDate } from "@/lib/api";

function Kpi({ icon: Icon, label: lbl, value, sub, to, testid, tone = "text-red-500" }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(to)} data-testid={testid} className="kpi text-left bg-card border border-zinc-800 rounded-xl p-4 flex items-center gap-4 group">
      <span className={`kpi-ring h-12 w-12 shrink-0 rounded-full grid place-items-center ${tone}`}><Icon size={20} /></span>
      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.12em] text-zinc-400 truncate">{lbl}</span>
        <span className="block font-display text-2xl font-bold text-white mt-0.5 truncate">{value}</span>
        {sub && <span className="block text-[11px] text-zinc-500 truncate">{sub}</span>}
      </span>
    </button>
  );
}

const MiniList = ({ rows, to, empty, render }) => {
  const nav = useNavigate();
  if (!rows?.length) return <div className="text-sm text-zinc-500 py-4">{empty}</div>;
  return <div className="divide-y divide-zinc-800/70">{rows.map((r) => (
    <button key={r.id} onClick={() => nav(to(r))} className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-zinc-800/30 px-1 rounded">{render(r)}</button>
  ))}</div>;
};

function ActiveRentals() {
  const [rows, setRows] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/rentals", { params: { status: "aperto", imported: "false" } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <section className="led bg-card rounded-xl p-4 sm:p-5" data-testid="active-rentals-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="panel-title font-display font-semibold flex items-center gap-2"><KeyRound size={16} />Noleggi in corso <span className="text-xs text-zinc-400" data-testid="active-rentals-count">({rows?.length ?? "…"})</span></h3>
        <button className="text-lg font-bold text-brand flex items-center gap-1" onClick={() => nav("/calendario?tipo=noleggi")} data-testid="active-rentals-calendar">Vai a calendario<ArrowUpRight size={18} /></button>
      </div>
      <MiniList rows={rows?.slice(0, 8)} to={(x) => `/noleggi/${x.id}`} empty={rows ? "Nessun noleggio in corso" : "Caricamento…"}
        render={(x) => <><span className="text-sm truncate"><span className="font-mono text-xs text-zinc-500 mr-2">{x.code}</span>{x.customer_name} · {x.vehicle_label}</span><span className="text-xs text-zinc-400 shrink-0">fino al {fmtDateTime(x.planned_end)}</span></>} />
    </section>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const { can } = useAuth();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(notifyErr); }, []);
  if (!d) return <div className="text-zinc-500">Caricamento dashboard…</div>;
  const v = d.vehicles;
  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="text-4xl sm:text-5xl font-bold text-white">Dashboard</h1>
        <p className="text-sm text-zinc-400 mt-1">Situazione aggiornata al {new Date().toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" })}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up">
        {can("rentals", "create") && <button onClick={() => nav("/noleggi?crea=1")} data-testid="dashboard-new-contract-btn"
          className="led bg-card rounded-xl p-6 text-left flex items-center gap-5 group hover:-translate-y-0.5 transition-transform">
          <span className="h-14 w-14 shrink-0 rounded-full grid place-items-center btn-brand"><FileSignature size={24} /></span>
          <span><span className="block font-display text-2xl font-bold text-white">Nuovo contratto</span><span className="block text-sm text-zinc-400">Crea un noleggio e stampa il contratto</span></span>
        </button>}
        <div className={can("rentals", "create") ? "lg:col-span-2" : "lg:col-span-3"}><ActiveRentals /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 fade-up">
        <Kpi icon={CalendarCheck} label="Prenotazioni oggi" value={d.bookings_today} to="/calendario?tipo=prenotazioni" testid="kpi-bookings-today" tone="text-red-500" />
        <Kpi icon={Car} label="Disponibili" value={v.disponibile} sub={`su ${d.vehicles_total} veicoli`} to="/veicoli?status=disponibile" testid="kpi-available" tone="text-emerald-400" />
        <Kpi icon={CalendarClock} label="Prenotati" value={v.prenotato} to="/veicoli?status=prenotato" testid="kpi-reserved" tone="text-sky-400" />
        <Kpi icon={KeyRound} label="A noleggio" value={v.in_noleggio} to="/veicoli?status=in_noleggio" testid="kpi-rented" tone="text-red-400" />
        <Kpi icon={Wrench} label="In manutenzione" value={v.in_manutenzione} to="/veicoli?status=in_manutenzione" testid="kpi-maintenance" tone="text-orange-400" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 fade-up">
        <Kpi icon={Wallet} label="Da incassare" value={fmtMoney(d.to_collect)} to="/pagamenti?status=da_incassare" testid="kpi-to-collect" tone="text-violet-400" />
        <Kpi icon={AlertTriangle} label="Pagamenti in ritardo" value={d.overdue_count} sub={fmtMoney(d.overdue_amount)} to="/pagamenti?overdue=1" testid="kpi-overdue" tone="text-red-400" />
        <Kpi icon={Clock} label="Scadenze imminenti" value={d.deadlines_count} to="/scadenze?expiry=prossime" testid="kpi-deadlines" tone="text-orange-400" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title="Andamento incassi" className="xl:col-span-2" testid="revenue-chart">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 256 }}>
              <BarChart data={d.revenue_trend.map((x) => ({ ...x, label: `${x.month.slice(5)}/${x.month.slice(2, 4)}` }))}>
                <CartesianGrid stroke="#27272a" vertical={false} />
                <XAxis dataKey="label" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip formatter={(x) => fmtMoney(x)} contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Bar dataKey="amount" name="Incassi" fill="var(--brand)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-zinc-500 mt-2">Totale incassato (escluso storico importato): {fmtMoney(d.collected_total)}</div>
        </Panel>
        <Panel title="Scadenze imminenti" action={<button className="text-xs text-brand" onClick={() => nav("/scadenze")}>Vedi tutte</button>}>
          <MiniList rows={d.deadlines} to={() => "/scadenze"} empty="Nessuna scadenza imminente"
            render={(x) => <><span className="text-sm truncate">{x.type} · {x.vehicle_label}</span><StatusBadge value={x.expiry_state} text={fmtDate(x.due_date)} /></>} />
        </Panel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Consegne previste oggi" testid="pickups-today">
          <MiniList rows={d.pickups_today} to={(x) => (x.planned_start ? `/noleggi/${x.id}` : `/calendario?id=${x.id}`)} empty="Nessuna consegna prevista"
            render={(x) => <><span className="text-sm truncate">{x.customer_name} · {x.vehicle_label}</span><span className="text-xs text-zinc-400">{fmtDateTime(x.start || x.planned_start)}</span></>} />
        </Panel>
        <Panel title="Ultimi noleggi" action={<button className="text-xs text-brand flex items-center" onClick={() => nav("/noleggi")}>Tutti<ArrowUpRight size={12} /></button>}>
          <MiniList rows={d.latest_rentals} to={(x) => `/noleggi/${x.id}`} empty="Nessun noleggio"
            render={(x) => <><span className="text-sm truncate"><span className="font-mono text-xs text-zinc-500 mr-2">{x.code}</span>{x.customer_name}</span><StatusBadge value={x.status} /></>} />
        </Panel>
      </div>
      <Panel title="Calendario prossimi 14 giorni" action={<button className="text-xs text-brand" onClick={() => nav("/calendario")}>Apri calendario</button>} testid="week-calendar">
        <CalendarStrip days={14} />
      </Panel>
    </div>
  );
}
